import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useBom, bomApi, bomKeys, type PricingResult } from "@/lib/bom";
import type { BomLineItem } from "@/lib/bom";
import { parseQuantityCols, serializeQuantityCols } from "@/lib/quantity-cols";
import type { QuantityColumn } from "@/lib/quantity-cols";
import { BomHeaderStrip } from "./BomHeaderStrip";
import { QuantityColumnsEditor } from "./QuantityColumnsEditor";
import { BomLineItemsGrid } from "./BomLineItemsGrid";
import { BomTotalsBar } from "./BomTotalsBar";
import { TierBreakChart } from "./TierBreakChart";
import { BomLockedBanner } from "./BomLockedBanner";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BomEditorProps {
  bomId: number;
  parentType: "Quote" | "Job";
  parentId: number;
  productId: number | null;
}

// ---------------------------------------------------------------------------
// Parent query key helpers — used to invalidate line totals after mutations
// ---------------------------------------------------------------------------

function parentQueryKeys(
  parentType: "Quote" | "Job",
  parentId: number,
): readonly (readonly unknown[])[] {
  if (parentType === "Quote") {
    return [["quote", parentId], ["quote-items", parentId]];
  }
  // Job
  return [["job", parentId], ["jobs", parentId, "items"]];
}

// ---------------------------------------------------------------------------
// BomEditor
// ---------------------------------------------------------------------------

export function BomEditor({ bomId, parentType, parentId, productId }: BomEditorProps) {
  const queryClient = useQueryClient();
  const { data: bom, isLoading } = useBom(bomId);

  // Derived state
  const disabled = bom != null && bom.lifecycleStatus !== "Draft";
  // Memoized so a single cost edit (which doesn't touch quantityColsJson) does
  // not re-parse the columns JSON on every render.
  const cols: QuantityColumn[] = useMemo(
    () => parseQuantityCols(bom?.quantityColsJson),
    [bom?.quantityColsJson],
  );

  // Live pricing state
  const [pricing, setPricing] = useState<PricingResult | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Trigger live pricing whenever bom data changes (items or columns)
  useEffect(() => {
    if (!bom) return;
    const currentCols = parseQuantityCols(bom.quantityColsJson);
    if (currentCols.length === 0) return;

    let cancelled = false;

    // Clear any pending debounce
    if (debounceRef.current != null) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setPricingLoading(true);
      bomApi
        .price(bom.bomId, {
          quantities: currentCols.map((c) => c.quantity),
          overs: currentCols.map((c) => c.overs),
          markupPcts: currentCols.map((c) => c.markupPct),
          adjustAmts: currentCols.map((c) => c.adjustAmt),
        })
        .then((result) => {
          if (!cancelled) setPricing(result);
        })
        .catch(() => {
          /* price fetch failed — totals stay stale; loading clears in finally */
        })
        .finally(() => {
          if (!cancelled) setPricingLoading(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      if (debounceRef.current != null) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bomId, bom?.items, bom?.quantityColsJson]);

  // Shared invalidation after any mutation
  async function invalidateAfterMutation() {
    await queryClient.invalidateQueries({ queryKey: bomKeys.detail(bomId) });
    await queryClient.invalidateQueries({ queryKey: bomKeys.byParent(parentType, parentId) });
    for (const key of parentQueryKeys(parentType, parentId)) {
      await queryClient.invalidateQueries({ queryKey: key });
    }
  }

  // ---------------------------------------------------------------------------
  // Mutation handlers
  // ---------------------------------------------------------------------------

  async function handleHeaderSave(patch: Record<string, unknown>) {
    await bomApi.patch(bomId, patch, bom?.rowVersion ?? null);
    await invalidateAfterMutation();
  }

  async function handleColumnsChange(next: QuantityColumn[]) {
    await bomApi.patch(
      bomId,
      { quantityColsJson: serializeQuantityCols(next) },
      bom?.rowVersion ?? null,
    );
    await invalidateAfterMutation();
  }

  async function handleAddItem(req: Record<string, unknown>) {
    await bomApi.addItem(bomId, req);
    await invalidateAfterMutation();
  }

  async function handlePatchItem(item: BomLineItem, patch: Record<string, unknown>) {
    await bomApi.patchItem(bomId, item.bomLineItemId, patch, item.rowVersion);
    await invalidateAfterMutation();
  }

  async function handleDeleteItem(item: BomLineItem) {
    await bomApi.deleteItem(bomId, item.bomLineItemId, item.rowVersion);
    await invalidateAfterMutation();
  }

  async function handleReorder(order: { bomLineItemId: number; sortOrder: number }[]) {
    await bomApi.reorder(bomId, order);
    await invalidateAfterMutation();
  }

  async function handleApplyTemplate(templateId: number) {
    await bomApi.applyTemplate(bomId, templateId, true);
    await invalidateAfterMutation();
  }

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (isLoading || !bom) {
    return (
      <div role="status" aria-label="Loading BOM" className="py-8 flex justify-center">
        <span className="animate-spin text-muted-foreground text-xl" aria-hidden="true">
          ◌
        </span>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col gap-4">
      {/* Locked banner */}
      {disabled && <BomLockedBanner reason={bom.lockedReason} />}

      {/* Header (product name, deco method, currency, etc.) */}
      <section className="bg-white rounded-xl border border-slate-300 shadow-sm p-5">
        <h3 className="text-base font-bold text-[#0f3453] mb-4 border-b border-slate-100 pb-2">Core Details</h3>
        <BomHeaderStrip
          bom={bom}
          productId={bom.sourceProductId ?? productId}
          disabled={disabled}
          onSave={handleHeaderSave}
          onApplyTemplate={(templateId) => { void handleApplyTemplate(templateId); }}
        />
      </section>

      {/* Single-column layout */}
      <div className="flex flex-col gap-6">
        {/* Pricing Columns */}
        <section className="bg-white rounded-xl border border-slate-300 shadow-sm p-5 overflow-hidden">
          <h3 className="text-base font-bold text-[#0f3453] mb-4 border-b border-slate-100 pb-2">Pricing Columns</h3>
          <QuantityColumnsEditor
            columns={cols}
            pricing={pricing}
            disabled={disabled}
            onChange={(next) => { void handleColumnsChange(next); }}
          />
        </section>

        {/* Pricing Curve */}
        <section className="bg-white rounded-xl border border-slate-300 shadow-sm p-5">
          <h3 className="text-base font-bold text-[#0f3453] mb-4 border-b border-slate-100 pb-2">Pricing Curve</h3>
          <TierBreakChart result={pricing} />
        </section>

        {/* Components */}
        <section className="bg-white rounded-xl border border-slate-300 shadow-sm p-5 overflow-hidden">
          <h3 className="text-base font-bold text-[#0f3453] mb-4 border-b border-slate-100 pb-2">Components</h3>
          <BomLineItemsGrid
            items={bom.items}
            pricing={pricing}
            disabled={disabled}
            onAddItem={(req) => { void handleAddItem(req); }}
            onPatchItem={(item, patch) => { void handlePatchItem(item, patch); }}
            onDeleteItem={(item) => { void handleDeleteItem(item); }}
            onReorder={(order) => { void handleReorder(order); }}
            bomColours={bom.colours ?? null}
            bomPositions={bom.positions ?? null}
          />
        </section>
        
        <div className="bg-white rounded-xl border border-slate-300 shadow-sm p-4 sticky bottom-4 z-10">
          <BomTotalsBar result={pricing} loading={pricingLoading} />
        </div>
      </div>
    </div>
  );
}
